import React, { useEffect, useState } from "react";
import { Dialog } from "@mui/material";
import {
  bindTabIdentity,
  unbindTabIdentity,
  isTabSessionStale,
  getStoredIdentity,
} from "../../Utility/tabSession";
import "./TabSessionGuard.css";

// ---------------------------------------------------------------------------
// Blocks a tab whose account was switched/logged out from another tab (see
// Utility/tabSession.js). Mounted inside ProtectedRoute, so it only runs on
// logged-in screens. Not dismissable — the only way out is a full reload, so
// no component state from the old account survives.
// ---------------------------------------------------------------------------
const TabSessionGuard = () => {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    bindTabIdentity();

    // 'storage' fires only in OTHER tabs, exactly when they login/logout.
    // visibilitychange is a fallback for when the tab is switched back to.
    const check = () => setStale(isTabSessionStale());
    const onVisibility = () => {
      if (document.visibilityState === "visible") check();
    };

    window.addEventListener("storage", check);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("storage", check);
      document.removeEventListener("visibilitychange", onVisibility);
      unbindTabIdentity();
    };
  }, []);

  const phone = getStoredIdentity();
  const loggedIn = Boolean(phone);
  const newName = localStorage.getItem("FullName");

  const handleContinue = () => {
    // Full reload (not navigate) — drops every screen's cached state of the
    // old account. Dashboard instead of the current URL, since the current
    // record may not belong to the new account.
    window.location.assign(loggedIn ? "/dashboard" : "/");
  };

  return (
    <Dialog
      open={stale}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown
      className="tab-session-dialog"
    >
      <div className={`tab-session${loggedIn ? "" : " tab-session--logout"}`}>
        <div className="tab-session__hero">
          <div className="tab-session__icon">
            <i className={loggedIn ? "bx bx-transfer-alt" : "bx bx-log-out-circle"} />
          </div>
          <h5 className="tab-session__title">
            {loggedIn ? "Account Changed" : "Session Ended"}
          </h5>
          <p className="tab-session__subtitle">
            {loggedIn
              ? "Another account was logged in from a different tab"
              : "You were logged out from a different tab"}
          </p>
        </div>

        <div className="tab-session__body">
          {loggedIn ? (
            <div className="tab-session__account">
              <div className="tab-session__avatar">
                {(newName || phone || "?").charAt(0)}
              </div>
              <div>
                <p className="tab-session__account-label">Now active</p>
                <p className="tab-session__account-name">{newName || phone}</p>
                {newName && <p className="tab-session__account-phone">{phone}</p>}
              </div>
            </div>
          ) : (
            <div className="tab-session__account">
              <div className="tab-session__avatar">
                <i className="bx bx-lock-alt" />
              </div>
              <div>
                <p className="tab-session__account-label">Status</p>
                <p className="tab-session__account-name">Not logged in</p>
              </div>
            </div>
          )}

          <p className="tab-session__text">
            {loggedIn
              ? "Only one account can be active per browser at a time. This screen still shows the previous account's data, so nothing from it was saved."
              : "Please log in again to continue. Nothing from this screen was saved."}
          </p>

          <div className="tab-session__tip">
            <i className="bx bx-bulb" />
            <span>
              Need two accounts together? Open the second one in an{" "}
              <strong>Incognito window</strong> or a different browser.
            </span>
          </div>

          <button type="button" className="tab-session__btn" onClick={handleContinue}>
            {loggedIn ? "Continue with this account" : "Go to Login"}
            <i className="bx bx-right-arrow-alt" />
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default TabSessionGuard;
